import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router';

const Ctx = createContext<{ alvo: HTMLElement | null; setAlvo: (e: HTMLElement | null) => void } | null>(null);

export function ProvedorAcaoPrimaria({ children }: { children: ReactNode }) {
  const [alvo, setAlvo] = useState<HTMLElement | null>(null);
  return <Ctx.Provider value={{ alvo, setAlvo }}>{children}</Ctx.Provider>;
}

/** Espera máxima pelo botão primário da aba depois de uma ação rápida do Início. */
const ESPERA_ACAO_RAPIDA_MS = 4000;

/**
 * Fase 2 (W1, D31): a ação rápida do Início chega com `state.acaoRapida`. Assim que a aba põe o botão primário no slot,
 * ele é acionado uma vez (o formulário de novo registro abre) e o estado sai do histórico (voltar e recarregar não
 * reabrem). Sem botão primário em até 4 s (sem permissão, aba sem ação), nada acontece.
 */
function useAcaoRapida(alvo: HTMLElement | null) {
  const local = useLocation();
  const navegar = useNavigate();
  const pedida = !!(local.state as { acaoRapida?: boolean } | null)?.acaoRapida;
  useEffect(() => {
    if (!pedida || !alvo) return;
    let feito = false;
    const limpar = () => navegar(`${local.pathname}${local.search}`, { replace: true, state: null });
    const tentar = () => {
      const botao = alvo.querySelector<HTMLButtonElement>('button[data-variante="primario"]:not([aria-disabled="true"]):not([disabled])');
      if (!botao || feito) return;
      feito = true;
      obs.disconnect();
      clearTimeout(prazo);
      limpar();
      botao.click();
    };
    const obs = new MutationObserver(tentar);
    obs.observe(alvo, { childList: true, subtree: true, attributes: true });
    const prazo = setTimeout(() => {
      obs.disconnect();
      if (!feito) limpar();
    }, ESPERA_ACAO_RAPIDA_MS);
    tentar();
    return () => {
      obs.disconnect();
      clearTimeout(prazo);
    };
  }, [pedida, alvo, local.pathname, local.search, navegar]);
}

/** Lugar no cabeçalho da página de seção onde a aba coloca o botão primário (F6). */
export function SlotAcaoPrimaria() {
  const c = useContext(Ctx);
  useAcaoRapida(c?.alvo ?? null);
  return <div ref={(el) => c?.setAlvo(el)} data-tour="acao-primaria" className="flex max-w-full min-w-0 flex-wrap items-center gap-2" />;
}

/** Usado pela aba: renderiza o botão primário no cabeçalho da seção. */
export function AcaoPrimaria({ children }: { children: ReactNode }) {
  const c = useContext(Ctx);
  const [montado, setMontado] = useState(false);
  useEffect(() => setMontado(true), []);
  if (!montado || !c?.alvo) return null;
  return createPortal(children, c.alvo);
}
